import { Box, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import {
  MarketplaceSubEvent,
  OfferActionSurface,
} from '../../../../../../../services/analytics/marketplaceEvents';
import { usePatchLeadByExternalIdOffersOfferId } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdOffersOfferId';
import { formatMoney } from '../../../../../../../utils/helpers';
import { useOfferAnalytics } from '../OfferAnalyticsContext';
import { useOfferActionFeedback } from '../useOfferActionFeedback';
import { MarketplaceOffer, OfferCardView, getOfferErrorMessage, isOfferStale } from '../utils';
import CounterNoteField, { isCounterNoteValid } from './CounterNoteField';
import MoneyInput from './MoneyInput';
import OfferActionModal from './OfferActionModal';
import OfferModalActions from './OfferModalActions';
import StaleOfferBanner from './StaleOfferBanner';

type AdjustCounterModalProps = {
  card: OfferCardView;
  surface: OfferActionSurface;
  liveOffers: MarketplaceOffer[];
  externalId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const AdjustCounterModal = ({
  card,
  surface,
  liveOffers,
  externalId,
  onClose,
  onSuccess,
}: AdjustCounterModalProps) => {
  const [rawDigits, setRawDigits] = useState('');
  const [note, setNote] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { refetchOffers } = useOfferActionFeedback(externalId);
  const { trackOfferEvent } = useOfferAnalytics();
  const { mutate: editCounter } = usePatchLeadByExternalIdOffersOfferId();
  const isStale = isOfferStale(card.driverCounter ?? undefined, liveOffers);

  const currentCounter = card.driverCounterAmount ?? 0;
  const parsedAmount = rawDigits === '' ? 0 : Number(rawDigits);
  const isValid = parsedAmount > 0 && isCounterNoteValid(note);

  const eventContext = {
    offerId: card.offer.id,
    dealerLabel: card.dealerLabel,
    dealerAmount: card.amount,
    surface,
  };

  const trackAdjustFailed = () =>
    trackOfferEvent(MarketplaceSubEvent.OfferActionFailed, {
      ...eventContext,
      actionType: 'adjust',
    });

  const handleSubmit = () => {
    setIsPending(true);
    setErrorMessage(null);
    // Atomic edit: the server replaces the pending counter in one transaction and runs the
    // ceiling/grace check first, so a rejection (e.g. above the locked ceiling) leaves the
    // standing counter untouched — no destructive withdraw on a fat-fingered amount.
    editCounter(
      {
        externalId,
        offerId: card.driverCounter?.id ?? '',
        data: { action: 'edit', amount: parsedAmount, notes: note.trim() },
      },
      {
        onSuccess: () => {
          trackOfferEvent(MarketplaceSubEvent.OfferAdjustSubmitted, {
            ...eventContext,
            driverAmount: parsedAmount,
          });
          refetchOffers();
          onSuccess();
          setIsPending(false);
          onClose();
        },
        onError: (error) => {
          trackAdjustFailed();
          setErrorMessage(getOfferErrorMessage(error) ?? 'Something went wrong, please try again.');
          setIsPending(false);
        },
      },
    );
  };

  const handleEnter = () => {
    if (isValid && !isPending) handleSubmit();
  };

  return (
    <OfferActionModal
      isOpen
      onClose={isPending ? () => undefined : onClose}
      title={`Adjust your counter on Offer ${card.dealerLabel}`}
    >
      <Box px={{ base: '24px', md: '32px' }} pb={{ base: '24px', md: '32px' }} pt="16px">
        {isStale && <StaleOfferBanner />}

        <VStack align="stretch" spacing="4px" mb="20px">
          <Text fontSize="13px" color="gray.500">
            Their offer: <strong>{formatMoney(card.amount)}</strong>
          </Text>
          <Text fontSize="13px" color="gray.500">
            Your current counter: <strong>{formatMoney(currentCounter)}</strong>
          </Text>
        </VStack>

        <Text fontSize="13px" fontWeight="600" color="leaseEndBlue" mb="4px">
          What's your new counter?
        </Text>

        <MoneyInput
          rawDigits={rawDigits}
          onDigitsChange={(digits) => {
            setRawDigits(digits);
            setErrorMessage(null);
          }}
          isWarn={errorMessage != null}
          onEnter={handleEnter}
        />

        {errorMessage && (
          <Text fontSize="12px" color="orange.500" mb="6px" fontWeight="500">
            {errorMessage}
          </Text>
        )}

        <CounterNoteField note={note} onNoteChange={setNote} />

        <Text mt="16px" fontSize="11.5px" color="gray.400" lineHeight="1.5" mb="8px">
          Adjusting keeps the negotiation open. To cancel entirely, use Withdraw instead.
        </Text>

        <OfferModalActions
          mt="20px"
          onCancel={onClose}
          onPrimary={handleSubmit}
          primaryLabel="Update Counter"
          isPending={isPending}
          isPrimaryDisabled={!isValid || isStale}
        />
      </Box>
    </OfferActionModal>
  );
};

export default AdjustCounterModal;
